import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useDrop } from 'react-dnd';
import IconUploadImage from '../../assets/icons/IconUploadImage';
import CustomTooltip from '../../ui/atoms/CustomTooltip'; // Adjust the import path
import useImages from '../MainComponent/hooks/useImages';
import { RootState, useAppSelector } from '../../redux/Store';
import { useDispatch, useSelector } from 'react-redux';
import {
  AuditStateMediaItem,
  MobileStateMediaItem,
} from '@safeguardproperties/corescript-react-npm/dist/types';
import ImageDialog from './ImageDialog';
import useMediaLabels from '../MainComponent/hooks/useMediaLabels';
import {
  addMediaLabelToImage,
  moveMediasFromLabel,
} from '../../redux/reducer/coreScriptReducer';
import { debounce } from '../../utils';
import IconRemove from '../../assets/icons/IconRemove';
import IconMove from '../../assets/icons/IconMove';
import IconCopy from '../../assets/icons/IconCopy';
import MultiKebab from '../../ui/organisms/MenuItems/MultiKebab';
import { ImageMetadata } from '../../types/ui';
import useMediaHelper from '../MainComponent/hooks/useMediaHelper';

import useQueryObject from '../../hooks/useQueryObject';

type LabelMediaItemProps = {
  item: AuditStateMediaItem | MobileStateMediaItem;
  index: number;
  type?: 'question' | 'label';

  selected?: boolean;
  expanded?: boolean;
};

const LabelMediaItem = ({
  index,
  item,
  expanded,
  type = 'label',
}: LabelMediaItemProps) => {
  const dispatch = useDispatch();

  const cloneQueue = useAppSelector((state) => state.gallery.cloneQueue);
  // const assignedMedia = useAttachedMediaDataByLabel(item.Label);
  const menuOptions = useLabelMenuOptions(item, () => {
    setIsMenuOpen(false);
  });

  const isReadOnly = useSelector(
    (state: RootState) => state.contextId.isReadOnly
  );

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const previewImage = useMemo(() => {
    const { Thumb }: any = item;

    if (Thumb?.uri) {
      return Thumb.uri;
    }

    // Fallback to last thumb in Thumb array, if available
    if (Array.isArray(Thumb) && Thumb.length > 0) {
      return Thumb.at(-1);
    }

    return '';
  }, [item]);

  const label = item?.Label?.toLanguagePack();
  const [imagePreview, setImagesPreview] = useState<boolean>(false);

  const statusColor = useMemo(() => {
    if (item.Min === undefined) {
      return '#4CAF50';
    }
    if (item.Min === 0) {
      return '#4CAF50';
    }
    if (item.Have === undefined) {
      return '#F44336'; // Red for missing Have
    }
    if (item.Have >= item.Min) {
      return '#4CAF50'; // Green greater or equal to Min
    }
    return '#F44336'; // Red for missing Have
  }, [item]);

  const { getAllImagesByLabel, getAllImagesByQuestion } = useMediaHelper();

  const images = useMemo(() => {
    if (!imagePreview) return [];
    if (type === 'question') {
      return getAllImagesByQuestion(item.QuestionName);
    }

    return getAllImagesByLabel(item.Label);
  }, [
    imagePreview,
    type,
    getAllImagesByLabel,
    item.Label,
    item.QuestionName,
    getAllImagesByQuestion,
  ]);

  const imageIndex = useMemo(() => {
    if ((item.Thumb as any)?.id) {
      return images?.findIndex((img) => img.guid === (item.Thumb as any)?.id);
    }
    return 0;
  }, [images, item.Thumb]);

  useEffect(() => {
    if (!previewImage) {
      setIsMenuOpen(false);
    }
  }, [previewImage]);

  useEffect(() => {
    if (!images || (images.length === 0 && imagePreview)) {
      setImagesPreview(false);
    }
  }, [images, imagePreview]);

  const onDropImageLabel = useCallback(
    (draggedObjects: any) => {
      // Support both direct array and { images, indices } object
      const imagesArray: ImageMetadata[] = Array.isArray(draggedObjects)
        ? draggedObjects
        : Array.isArray(draggedObjects?.images)
          ? draggedObjects.images
          : [];

      if (!Array.isArray(imagesArray) || imagesArray.length === 0) return;

      const validMedias = imagesArray
        .filter((obj) => obj.guid && obj.webFileName)
        .map((obj) => ({
          Guid: obj.guid,
          ThumbUri: obj.webFileName,
        }));

      if (!item.Label || validMedias.length === 0) {
        return console.error(
          'Guid or Media Label not found in dragged objects or item',
          draggedObjects,
          item
        );
      }

      dispatch(
        addMediaLabelToImage({
          label: item.Label || '',
          media: validMedias,
        })
      );
    },
    [dispatch, item]
  );

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: 'image',
      drop: debounce(onDropImageLabel, 10),
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
      }),
    }),
    [onDropImageLabel, debounce]
  );

  const truncatedLabelName =
    label && label.length > 10 ? `${label.slice(0, 10)}...` : label;

  return (
    <>
      <Box
        ref={drop}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          width: expanded ? '200px' : '100px',
          padding: 0,
          alignItems: 'center',
          gap: '8px',
          flexShrink: 0,
          borderRadius: expanded ? '8px' : '10px',
          border: isOver ? '2px dashed #1D6EC9' : '1px solid #D8E0E5',
          background: '#FFF',
          cursor: previewImage ? 'pointer' : 'default',
          overflow: 'hidden',
          transition: 'width 0.3s ease-in-out', // Smooth transition for expanded width
          '@media (max-width: 600px)': {
            // Adjustments for smaller screens
            width: '100%',
            padding: '4px',
          },
        }}
        onDoubleClick={() => {
          if (previewImage) {
            setImagesPreview(true);
          }
        }}
      >
        {Boolean(cloneQueue && cloneQueue.includes(item.Label)) && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
            }}
          >
            <CircularProgress size={32} color='primary' />
          </Box>
        )}

        {/* Image Section */}
        <Box
          sx={{
            width: '100%',
            height: 0,
            paddingTop: '75%', // Fixed aspect ratio (4:3)
            backgroundColor: '#F4F4F4', // Placeholder for no image
            position: 'relative',
          }}
        >
          {previewImage ? (
            <img
              src={previewImage}
              alt={label || ''}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <Box
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <IconUploadImage />
            </Box>
          )}
        </Box>

        {/* Label and Kebab Menu (Horizontally Aligned) */}
        <Box
          sx={{
            width: '100%',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between', // Justifies space between label and kebab menu
            marginTop: '-8px',
            height: '50px',
          }}
        >
          <Typography
            sx={{
              color: '#343330',
              fontFamily: 'Montserrat',
              fontSize: expanded ? '12px' : '10px',
              fontWeight: 500,
              textTransform: 'capitalize',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 3, // Limit to 3 lines
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.5,
              textAlign: 'left', // Align the text to the left
              flex: 1, // Ensures the label takes available space
            }}
          >
            <CustomTooltip label={label} placement='top' arrow>
              <span>{label}</span>
            </CustomTooltip>
          </Typography>
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              cursor: 'pointer',
            }}
          >
            <MultiKebab
              disabled={!previewImage || isReadOnly}
              options={menuOptions}
              isMenuOpen={isMenuOpen}
              setIsMenuOpen={(value: boolean) => {
                if (!previewImage) return;
                setIsMenuOpen(value);
              }}
            />
          </Box>
        </Box>

        {/* Status Badge */}
        <Box
          sx={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            backgroundColor: statusColor,
            color: '#FFF',
            padding: '4px 8px',
            borderRadius: '12px',
            fontFamily: 'Montserrat',
            fontSize: '12px',
            fontWeight: 500,
            zIndex: 3,
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          {item.Have !== undefined && item.Min !== undefined
            ? `${item.Have}/${item.Min}`
            : item.Have === undefined
              ? item.Min === undefined
                ? ''
                : `${item.Min}`
              : `${item.Have}`}
        </Box>
      </Box>

      {/* Image Dialog */}
      {images && images.length > 0 && (
        <ImageDialog
          open={imagePreview}
          initialIndex={imageIndex}
          images={images}
          onClose={() => setImagesPreview((prev) => !prev)}
        />
      )}
    </>
  );
};

const useLabelMenuOptions = (
  item: AuditStateMediaItem | MobileStateMediaItem,
  hideMenu?: () => void
) => {
  const dispatch = useDispatch();
  const { processImageCloneRequest } = useImages();
  const { removeMediasByLabel, getMediasByLabel } = useMediaHelper();
  const { mediaLabels, labels } = useMediaLabels();
  const { cloneAllowed } = useQueryObject();

  const handleMoveToAnotherLabel = debounce((targetLabel: string) => {
    const id = (item as any).Thumb?.id;
    const guids = getMediasByLabel(item.Label).map((mLabel) => mLabel.Guid);

    dispatch(
      moveMediasFromLabel({
        fromLabel: item?.Label,
        toLabel: targetLabel,
        guids: id ? [id] : guids,
      })
    );

    hideMenu?.();
  }, 50);

  const handleCloneToAnotherLabel = debounce((targetLabel: string[]) => {
    const id = (item as any).Thumb?.id;
    const guids = getMediasByLabel(item.Label).map((mLabel) => mLabel.Guid);

    for (const label of targetLabel) {
      processImageCloneRequest(label, id ? [id] : guids);
    }
    hideMenu?.();
  }, 50);

  const onRemoveLabel = useCallback(() => {
    const id = (item as any).Thumb?.id;
    removeMediasByLabel(item.Label, id ? [id] : undefined);
    hideMenu?.();
  }, [item, hideMenu, removeMediasByLabel]);

  return useMemo(() => {
    const filteredLabels = labels.filter((label) => label !== item.Label);
    const sortedLabels = filteredLabels.sort((a, b) => {
      const labelObjA = mediaLabels?.find((item) => item?.Label === a);
      const labelObjB = mediaLabels?.find((item) => item?.Label === b);

      const isResolvedA = labelObjA?.Complete;
      const isResolvedB = labelObjB?.Complete;

      return Number(isResolvedA) - Number(isResolvedB);
    });

    return [
      {
        id: '1',
        label: 'Remove Label',
        onClick: onRemoveLabel,
        prefixIcon: <IconRemove />,
      },
      {
        id: '2',
        label: 'Move to another label',
        subOptions: sortedLabels.map((label) => {
          const labelObj = mediaLabels.find((item) => item.Label === label);
          return {
            id: label,
            label: label.toLanguagePack(),
            subtitle: labelObj?.Complete
              ? 'Resolved'
              : `${labelObj?.Have}/${labelObj?.Min}`,
            required: labelObj?.Required,
            onClick: () => handleMoveToAnotherLabel(label),
          };
        }),
        prefixIcon: <IconMove />,
        multiselect: false,
      },
      {
        id: '3',
        label: 'Clone to another label',
        disabled: !cloneAllowed,
        subOptions: sortedLabels.map((label) => {
          const labelObj = mediaLabels.find((item) => item.Label === label);
          // const isResolved = Number(labelObj?.Have) >= Number(labelObj?.Min);
          return {
            id: label,
            label: label.toLanguagePack(),
            subtitle: labelObj?.Complete
              ? 'Resolved'
              : `${labelObj?.Have}/${labelObj?.Min}`,
            required: labelObj?.Required,
          };
        }),
        prefixIcon: <IconCopy />,
        multiselect: true,
        onMultiselectDismiss: (values: string[]) => {
          handleCloneToAnotherLabel(values);
        },
      },
    ];
  }, [
    labels,
    onRemoveLabel,
    item.Label,
    mediaLabels,
    handleMoveToAnotherLabel,
    handleCloneToAnotherLabel,
    cloneAllowed,
  ]);
};

// const useAttachedMediaDataByLabel = (currentLabel: string) => {
//   const { Media: savedDataMedia } = useAppSelector(
//     (state) => state.coreScript.coreScript.saveData
//   );

//   const attachedMedia = useMemo(() => {
//     const list: Media[] = [];
//     const guids: string[] = [];

//     const mediaObjects = Object.values(savedDataMedia || {});
//     for (const media of mediaObjects) {
//       if (media.Guid && media.Label.includes(currentLabel)) {
//         guids.push(media.Guid);
//         list.push({
//           Guid: media.Guid,
//           ThumbUri: media.Path,
//         });
//       }
//     }
//     return { guids, media: list };
//   }, [savedDataMedia, currentLabel]);

//   return attachedMedia;
// };

export default React.memo(LabelMediaItem);
